import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Plus, Trash2, Calendar, MapPin, Phone, User, DollarSign, Percent, Upload, Download, Users, FileText, Banknote } from 'lucide-react';
import { createRegistry } from '../lib/registry';
import { getFeeSettings } from '../lib/admin';
import { isAdmin } from '../lib/admin';
import { supabase } from '../lib/supabase';
import { parseExcelFile, parseCSVFile, generateSampleTemplate } from '../lib/tables';
import DatePicker from '../components/DatePicker';
import TemplateSelector from '../components/TemplateSelector';
import FeeSettingsForm from '../components/FeeSettingsForm';
import InvitationSettings from '../components/invitations/InvitationSettings';
import type { InvitationConfig } from '../types/invitation';
import { DEFAULT_THEME } from '../types/theme';
import { readSoundtrack, validDate, validTime } from '../lib/invitation';
import type { RegistryItem, CreateRegistryDTO } from '../types/registry';
import type { GuestListUpload } from '../types/tables';

export default function CreateRegistry() {
  const navigate = useNavigate();
  const sparklesRef = useRef<HTMLDivElement>(null);
  const [invitation, setInvitation] = useState<InvitationConfig>();
  const [loading, setLoading] = useState(false);
  const [invitationUploading, setInvitationUploading] = useState(false);
  const [error, setError] = useState('');
  const [currency, setCurrency] = useState('LKR');
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  


  const [registry, setRegistry] = useState({
    title: '',
    description: '',
    event_date: '',
    delivery_address: '',
    contact_number: '',
    thank_you_message: '',
    account_holder_name: '',
    account_number: '',
    bank_name: '',
    bank_branch_name: ''
  });

  const [eventDetails, setEventDetails] = useState({
    name: '',
    start_time: '',
    end_time: '',
    venue: '',
    address: '',
    dress_code: '',
    parking_info: '',
    max_attendees: '',
    special_instructions: ''
  });

  const [items, setItems] = useState<Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>[]>([]);
  
  const [uploadedGuests, setUploadedGuests] = useState<GuestListUpload[]>([]);
  const [guestListError, setGuestListError] = useState('');
  const [processingGuestList, setProcessingGuestList] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [feeSettings, setFeeSettings] = useState({
    feeResponsibility: 'GUEST' as 'COUPLE' | 'GUEST' | 'SPLIT',
    platformFeePercentage: 20,
    gatewayFeePercentage: 20
  });

  useEffect(() => {
    checkAdminStatus();
    loadSystemFeeSettings();

    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  const checkAdminStatus = async () => {
    try {
      const adminStatus = await isAdmin();
      setIsUserAdmin(adminStatus);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsUserAdmin(false);
    }
  };

  const loadSystemFeeSettings = async () => {
    try {
      const { platformFeeRate, gatewayFeeRate } = await getFeeSettings();
      setFeeSettings(prev => ({
        ...prev,
        platformFeePercentage: platformFeeRate,
        gatewayFeePercentage: gatewayFeeRate
      }));
    } catch (error) {
      console.error('Error loading system fee settings:', error);
      // Keep default values if loading fails
    }
  };

  const handleFeeSettingsChange = (values: {
    feeResponsibility: 'COUPLE' | 'GUEST' | 'SPLIT';
    platformFeePercentage: number;
    gatewayFeePercentage: number;
  }) => {
    if (!isUserAdmin) {
      // Non-admin users can only change fee responsibility
      setFeeSettings({
        ...feeSettings,
        feeResponsibility: values.feeResponsibility
      });
    } else {
      // Admin can update all values
      setFeeSettings(values);
    }
  };

  const addItem = () => {
    setItems([...items, {
      name: '',
      price: 0,
      url: '',
      priority: 'medium',
      allows_group_gifting: false,
      target_amount: null,
      is_money_gift: false,
      money_amount: null
    }]);
  };

  const updateItem = (index: number, field: string, value: any) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleTemplateSelect = (templateItems: Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>[]) => {
    setItems(templateItems);
  };

  const handleGuestListFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setProcessingGuestList(true);
    setGuestListError('');
    setUploadedGuests([]);

    try {
      let parsedGuests: GuestListUpload[] = [];
      
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parsedGuests = await parseExcelFile(file);
      } else if (file.name.endsWith('.csv')) {
        parsedGuests = await parseCSVFile(file);
      } else {
        throw new Error('Unsupported file format. Please upload a CSV or Excel file.');
      }

      // For initial upload, set all table numbers to 0 (unassigned)
      const guestsWithUnassignedTables = parsedGuests.map(guest => ({
        ...guest,
        table_number: 0
      }));

      setUploadedGuests(guestsWithUnassignedTables);
    } catch (err) {
      console.error('Error parsing guest list file:', err);
      setGuestListError(err instanceof Error ? err.message : 'Failed to parse guest list file');
    } finally {
      setProcessingGuestList(false);
    }
  };

  const handleDownloadGuestTemplate = () => {
    generateSampleTemplate();
  };

  const clearGuestList = () => {
    setUploadedGuests([]);
    setGuestListError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePublish = async () => {
    if (invitationUploading) return;
    try {
      setLoading(true);
      setError('');

      // Validation
      if (!registry.title || !registry.event_date || !eventDetails.start_time || !eventDetails.venue || !registry.delivery_address || !registry.contact_number || items.length === 0) {
        setError('Please fill in all required fields and add at least one item');
        return;
      }

      if (invitation?.soundtrackUrl?.trim() && !readSoundtrack(invitation.soundtrackUrl)) {
        setError("Check the soundtrack link before saving.");
        setLoading(false);
        return;
      }
      if (invitation && (!eventDetails.name.trim() || invitation.timeline.some(t => !t.eventId && (!t.title.trim() || !validTime(t.time) || (t.date && !validDate(t.date)))) || (invitation.rsvpDeadline && (!validDate(invitation.rsvpDeadline) || invitation.rsvpDeadline > registry.event_date)))) {
        setError('Add a function name and check the invitation timeline and RSVP deadline.');
        return;
      }

      // Always get current system fee settings to ensure consistency
      const { platformFeeRate, gatewayFeeRate } = await getFeeSettings();

      const data: CreateRegistryDTO = {
        ...registry,
        ...(invitation ? { theme: { ...DEFAULT_THEME, invitation: { ...invitation, soundtrackUrl: readSoundtrack(invitation.soundtrackUrl)?.url || "" } } } : {}),
        currency,
        items: items.map(item => ({
          ...item,
          price: item.is_money_gift ? Number(item.money_amount) || 0 : Number(item.price) || 0,
          target_amount: item.is_money_gift ? null : (item.allows_group_gifting ? Number(item.target_amount) || 0 : null),
          allows_group_gifting: item.is_money_gift ? false : (item.allows_group_gifting || false),
          is_money_gift: item.is_money_gift || false,
          money_amount: item.is_money_gift ? Number(item.money_amount) || null : null,
          url: item.is_money_gift ? null : (item.url || null)
        })),
        guestList: uploadedGuests,
        fee_responsibility: feeSettings.feeResponsibility,
        // Store the rates as they are returned by getFeeSettings (already in percentage format)
        platform_fee_percentage: platformFeeRate,
        gateway_fee_percentage: gatewayFeeRate,

      };

      const newRegistry = await createRegistry(data);

      // Create event if details are provided
      if (eventDetails.name && eventDetails.start_time && eventDetails.venue) {
        const { error: eventError } = await supabase
          .from('registry_events')
          .insert({
            registry_id: newRegistry.id,
            name: eventDetails.name,
            date: registry.event_date,
            start_time: eventDetails.start_time,
            end_time: eventDetails.end_time || null,
            venue: eventDetails.venue,
            address: eventDetails.address,
            dress_code: eventDetails.dress_code || null,
            parking_info: eventDetails.parking_info || null,
            max_attendees: eventDetails.max_attendees ? parseInt(eventDetails.max_attendees) : null,
            special_instructions: eventDetails.special_instructions || null
          });

        if (eventError) {
          console.error('Error creating event:', eventError);
          // The registry already exists: send the owner to repair its functions, never retry creation.
          navigate(`/registries/${newRegistry.id}`, { state: { creationWarning: 'Your registry was created, but the function details could not be saved. Please add them using Edit Events before sharing the invitation.' } });
          return;
        }
      }

      navigate(`/registries/${newRegistry.id}`);
    } catch (err) {
      console.error('Error creating registry:', err);
      setError(err instanceof Error ? err.message : 'Failed to create registry');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold text-gray-800">Create Your Gift Registry</h1>
        <p className="text-gray-600">Set up your perfect gift registry for any occasion</p>
      </div>

      {error && (
        <div className="bg-red-100/60 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <InvitationSettings onUploadStateChange={setInvitationUploading} value={invitation} onChange={setInvitation} registry={{ id: 'creation-preview', title: registry.title || 'Your celebration', event_date: registry.event_date }} events={[]} giftRegistryAvailable={items.length > 0} previewEvents={eventDetails.name ? [{ ...eventDetails, id: 'draft-event', registry_id: 'creation-preview', date: registry.event_date, max_attendees: null, created_at: '' }] : []} />

      <div className="card-pastel p-6 space-y-8">
        {/* Registry Details */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Registry Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Registry Title *
              </label>
              <input
                type="text"
                value={registry.title}
                onChange={(e) => setRegistry({ ...registry, title: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Sarah & John's Wedding Registry"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Currency *
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="input-pastel"
              >
                <optgroup label="Major Currencies">
                  <option value="USD">US Dollar (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="AUD">Australian Dollar (AUD)</option>
                  <option value="CAD">Canadian Dollar (CAD)</option>
                  <option value="JPY">Japanese Yen (JPY)</option>
                  <option value="CHF">Swiss Franc (CHF)</option>
                  <option value="NZD">New Zealand Dollar (NZD)</option>
                </optgroup>
                <optgroup label="Asian Currencies">
                  <option value="LKR">Sri Lankan Rupee (LKR)</option>
                  <option value="SGD">Singapore Dollar (SGD)</option>
                  <option value="HKD">Hong Kong Dollar (HKD)</option>
                  <option value="MYR">Malaysian Ringgit (MYR)</option>
                  <option value="PHP">Philippine Peso (PHP)</option>
                  <option value="THB">Thai Baht (THB)</option>
                  <option value="TWD">Taiwan Dollar (TWD)</option>
                </optgroup>
                <optgroup label="European Currencies">
                  <option value="SEK">Swedish Krona (SEK)</option>
                  <option value="NOK">Norwegian Krone (NOK)</option>
                  <option value="DKK">Danish Krone (DKK)</option>
                  <option value="PLN">Polish Zloty (PLN)</option>
                  <option value="CZK">Czech Koruna (CZK)</option>
                  <option value="HUF">Hungarian Forint (HUF)</option>
                </optgroup>
                <optgroup label="Americas &amp; Other">
                  <option value="MXN">Mexican Peso (MXN)</option>
                  <option value="BRL">Brazilian Real (BRL)</option>
                  <option value="ILS">Israeli Shekel (ILS)</option>
                  <option value="ZAR">South African Rand (ZAR)</option>
                </optgroup>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select the currency for your registry.
              </p>
            </div>

            <div className="md:col-span-2">
              <DatePicker
                label="Event Date"
                value={registry.event_date}
                onChange={(date) => setRegistry({ ...registry, event_date: date })}
                required={true}
                placeholder="Select your event date"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Description
              </label>
              <textarea
                value={registry.description}
                onChange={(e) => setRegistry({ ...registry, description: e.target.value })}
                className="input-pastel"
                rows={3}
                placeholder="Tell your guests about your special occasion..."
              />
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Event Details</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Event Name *
              </label>
              <input
                type="text"
                value={eventDetails.name}
                onChange={(e) => setEventDetails({ ...eventDetails, name: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Wedding Reception"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Start Time *
                </label>
                <input
                  type="time"
                  value={eventDetails.start_time}
                  onChange={(e) => setEventDetails({ ...eventDetails, start_time: e.target.value })}
                  className="input-pastel"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  value={eventDetails.end_time}
                  onChange={(e) => setEventDetails({ ...eventDetails, end_time: e.target.value })}
                  className="input-pastel"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Venue *
              </label>
              <input
                type="text"
                value={eventDetails.venue}
                onChange={(e) => setEventDetails({ ...eventDetails, venue: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Grand Ballroom Hotel"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Address *
              </label>
              <input
                type="text"
                value={eventDetails.address}
                onChange={(e) => setEventDetails({ ...eventDetails, address: e.target.value })}
                className="input-pastel"
                placeholder="Full venue address"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Dress Code
              </label>
              <input
                type="text"
                value={eventDetails.dress_code}
                onChange={(e) => setEventDetails({ ...eventDetails, dress_code: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Formal, Cocktail, Casual"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Max Attendees
              </label>
              <input
                type="number"
                value={eventDetails.max_attendees}
                onChange={(e) => setEventDetails({ ...eventDetails, max_attendees: e.target.value })}
                className="input-pastel"
                placeholder="Expected number of guests"
                min="1"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Parking Information
              </label>
              <textarea
                value={eventDetails.parking_info}
                onChange={(e) => setEventDetails({ ...eventDetails, parking_info: e.target.value })}
                className="input-pastel"
                rows={2}
                placeholder="Parking instructions for guests..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Special Instructions
              </label>
              <textarea
                value={eventDetails.special_instructions}
                onChange={(e) => setEventDetails({ ...eventDetails, special_instructions: e.target.value })}
                className="input-pastel"
                rows={2}
                placeholder="Any special instructions for guests..."
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Contact & Delivery Information</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Delivery Address *
              </label>
              <textarea
                value={registry.delivery_address}
                onChange={(e) => setRegistry({ ...registry, delivery_address: e.target.value })}
                className="input-pastel"
                rows={3}
                placeholder="Where should gifts be delivered?"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Contact Number *
              </label>
              <input
                type="tel"
                value={registry.contact_number}
                onChange={(e) => setRegistry({ ...registry, contact_number: e.target.value })}
                className="input-pastel"
                placeholder="+94 77 123 4567"
                required
              />
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <DollarSign className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Bank Details for Money Transfers</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Account Holder Name *
              </label>
              <input
                type="text"
                value={registry.account_holder_name}
                onChange={(e) => setRegistry({ ...registry, account_holder_name: e.target.value })}
                className="input-pastel"
                placeholder="Full name as per bank account"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Account Number *
              </label>
              <input
                type="text"
                value={registry.account_number}
                onChange={(e) => setRegistry({ ...registry, account_number: e.target.value })}
                className="input-pastel"
                placeholder="Bank account number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Bank Name *
              </label>
              <input
                type="text"
                value={registry.bank_name}
                onChange={(e) => setRegistry({ ...registry, bank_name: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Commercial Bank of Ceylon"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Bank Branch Name *
              </label>
              <input
                type="text"
                value={registry.bank_branch_name}
                onChange={(e) => setRegistry({ ...registry, bank_branch_name: e.target.value })}
                className="input-pastel"
                placeholder="e.g., Colombo Main Branch"
                required
              />
            </div>
          </div>
        </div>



        {/* Fee Settings */}
        <FeeSettingsForm
          feeResponsibility={feeSettings.feeResponsibility}
          platformFeePercentage={feeSettings.platformFeePercentage}
          gatewayFeePercentage={feeSettings.gatewayFeePercentage}
          onChange={handleFeeSettingsChange}
          currency={currency}
          isAdmin={isUserAdmin}
        />

        {/* Template Selector */}
        <TemplateSelector onTemplateSelect={handleTemplateSelect} currency={currency} />

        {/* Gift Items */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Gift className="w-5 h-5 text-gray-800" />
              <h2 className="text-xl font-semibold text-gray-800">Gift Items</h2>
            </div>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center space-x-2 px-4 py-2 btn-pastel"
            >
              <Plus className="w-5 h-5" />
              <span>Add Item</span>
            </button>
          </div>

          {items.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-white/40 rounded-lg">
              <Gift className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No items added yet</p>
              <button
                type="button"
                onClick={addItem}
                className="btn-pastel"
              >
                Add Your First Item
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="bg-white/60 p-4 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-800">Item {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeItem(index)}
                      className="text-red-500 hover:text-red-400"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Item Name *
                      </label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(index, 'name', e.target.value)}
                        className="input-pastel"
                        placeholder="e.g., Coffee Maker"
                        required
                      />
                    </div>

                    {!item.is_money_gift && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Price ({currency}) *
                          </label>
                          <input
                            type="number"
                            value={item.price || ''}
                            onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                            className="input-pastel"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">
                            Product URL
                          </label>
                          <input
                            type="url"
                            value={item.url || ''}
                            onChange={(e) => updateItem(index, 'url', e.target.value)}
                            className="input-pastel"
                            placeholder="https://..."
                          />
                        </div>
                      </>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Priority
                      </label>
                      <select
                        value={item.priority}
                        onChange={(e) => updateItem(index, 'priority', e.target.value)}
                        className="input-pastel"
                      >
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {/* Money Gift Toggle */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`money-gift-${index}`}
                        checked={item.is_money_gift || false}
                        onChange={(e) => {
                          updateItem(index, 'is_money_gift', e.target.checked);
                          if (e.target.checked) {
                            updateItem(index, 'allows_group_gifting', false);
                          }
                        }}
                        className="h-4 w-4 bg-white/60 border-white/40 rounded text-pastel-purple-600 focus:ring-pastel-purple-400"
                      />
                      <label htmlFor={`money-gift-${index}`} className="ml-2 text-sm text-gray-600 flex items-center gap-1.5">
                        <Banknote className="w-4 h-4 text-pastel-green-600" />
                        Money Gift (multiple guests can claim)
                      </label>
                    </div>

                    {item.is_money_gift && (
                      <div className="space-y-2 pl-6 border-l-2 border-pastel-green-200">
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Preset Amount ({currency})
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                          {[5000, 10000, 15000, 20000].map(amt => (
                            <button
                              key={amt}
                              type="button"
                              onClick={() => {
                                updateItem(index, 'money_amount', amt);
                                if (!item.name || item.name.startsWith('Money Gift')) {
                                  updateItem(index, 'name', `Money Gift - ${currency} ${amt.toLocaleString()}`);
                                }
                              }}
                              className={`px-2 py-2 text-xs rounded-md font-medium transition-colors ${
                                item.money_amount === amt
                                  ? 'bg-pastel-green-500 text-white'
                                  : 'bg-white/60 border border-white/40 text-gray-600 hover:bg-white/80'
                              }`}
                            >
                              {currency} {amt.toLocaleString()}
                            </button>
                          ))}
                        </div>
                        <input
                          type="number"
                          value={item.money_amount || ''}
                          onChange={(e) => updateItem(index, 'money_amount', parseFloat(e.target.value) || 0)}
                          className="input-pastel"
                          placeholder="Or enter custom amount"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    )}

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={`group-gift-${index}`}
                        checked={item.allows_group_gifting || false}
                        onChange={(e) => updateItem(index, 'allows_group_gifting', e.target.checked)}
                        disabled={item.is_money_gift}
                        className="h-4 w-4 bg-white/60 border-white/40 rounded text-pastel-purple-600 focus:ring-pastel-purple-400 disabled:opacity-40"
                      />
                      <label htmlFor={`group-gift-${index}`} className="ml-2 text-sm text-gray-600">
                        Allow Group Gifting
                      </label>
                    </div>

                    {item.allows_group_gifting && !item.is_money_gift && (
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">
                          Target Amount ({currency}) *
                        </label>
                        <input
                          type="number"
                          value={item.target_amount || ''}
                          onChange={(e) => updateItem(index, 'target_amount', parseFloat(e.target.value) || 0)}
                          className="input-pastel"
                          placeholder="Target amount for group contributions"
                          min={item.price || 0}
                          step="0.01"
                          required
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Guest List Upload */}
        <div className="space-y-6">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Guest List Upload</h2>
          </div>

          <div className="bg-white/60 p-4 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-gray-600" />
                <span className="text-gray-800 font-medium">Upload Guest Names</span>
              </div>
              <button
                type="button"
                onClick={handleDownloadGuestTemplate}
                className="flex items-center space-x-1 px-3 py-1 text-sm bg-white/60 rounded-md hover:bg-white/80 text-gray-600 hover:text-gray-800"
              >
                <Download className="w-4 h-4" />
                <span>Download Template</span>
              </button>
            </div>
            
            <p className="text-sm text-gray-600">
              Upload a CSV or Excel file with guest names. Only guests on this list will be able to RSVP. You can assign table numbers later after receiving RSVPs.
            </p>
            
            {guestListError && (
              <div className="bg-red-100/60 border border-red-200 text-red-700 p-3 rounded-md">
                {guestListError}
              </div>
            )}

            <div className="flex flex-col space-y-4">
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/40 rounded-lg cursor-pointer bg-white/60 hover:bg-white/80 transition-colors">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">CSV or Excel files only</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleGuestListFileChange}
                  disabled={processingGuestList}
                />
              </label>
              
              {processingGuestList && (
                <div className="flex items-center justify-center space-x-2 text-gray-600">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-pastel-purple-600"></div>
                  <span>Processing guest list...</span>
                </div>
              )}

              {uploadedGuests.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-gray-800 font-medium">
                      Guest List Preview ({uploadedGuests.length} guests)
                    </h4>
                    <button
                      type="button"
                      onClick={clearGuestList}
                      className="text-red-500 hover:text-red-400 text-sm"
                    >
                      Clear List
                    </button>
                  </div>
                  
                  <div className="max-h-40 overflow-y-auto bg-white/60 rounded-lg p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {uploadedGuests.slice(0, 50).map((guest, index) => (
                        <div key={index} className="text-sm text-gray-600 flex items-center">
                          <User className="w-3 h-3 mr-2 text-gray-400" />
                          {guest.guest_name}
                        </div>
                      ))}
                      {uploadedGuests.length > 50 && (
                        <div className="text-sm text-gray-500 col-span-full text-center">
                          ...and {uploadedGuests.length - 50} more guests
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-blue-100/60 p-3 rounded-lg">
                    <p className="text-blue-700 text-sm">
                      ✓ Guest list uploaded successfully. Only these guests will be able to RSVP to your event.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Gift className="w-5 h-5 text-gray-800" />
            <h2 className="text-xl font-semibold text-gray-800">Thank You Message</h2>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Custom Thank You Message
            </label>
            <textarea
              value={registry.thank_you_message}
              onChange={(e) => setRegistry({ ...registry, thank_you_message: e.target.value })}
              className="input-pastel"
              rows={3}
              placeholder="Customize the thank you message shown after someone claims a gift..."
            />
            <p className="text-xs text-gray-500 mt-1">
              This message will be shown to guests after they claim a gift from your registry.
            </p>
          </div>
        </div>

        {/* Publish Button */}
        <div className="flex justify-end pt-6 border-t border-white/40">
          <button
            onClick={handlePublish}
            disabled={loading || invitationUploading}
            className="btn-pastel disabled:opacity-50"
          >
            {loading ? 'Creating Registry...' : 'Create Registry'}
          </button>
        </div>
      </div>
    </div>
  );
}